import type { Rights } from '../domain/entitlement';
import { currentRights } from '../domain/entitlement-snapshot';
import { packingStateSchema, validateStateReferences, type PackingState } from '../domain/schema';

export class TripConflictError extends Error {
  constructor() { super('다른 창에서 같은 내용을 바꿨어요. 내 초안과 저장된 원본을 덮어쓰지 않았습니다.'); this.name = 'TripConflictError'; }
}

export class TripCapacityError extends Error {
  constructor() { super('다른 창의 변경을 합치면 무료 한도인 준비물 120개·가방 4개를 넘어요. 초안을 파일로 보관한 뒤 수량을 조정해주세요. 기존 원본은 줄이거나 수정할 수 있어요.'); this.name = 'TripCapacityError'; }
}

export function assertTripCapacity(next: PackingState, previous?: PackingState, rights: Rights = currentRights()) {
  // Existing over-limit local data stays readable/editable after a refund; only
  // a new increase past the current value is refused.
  if (next.items.length > Math.max(rights.maxItemsPerTrip, previous?.items.length ?? 0) || next.bags.length > Math.max(rights.maxBagsPerTrip, previous?.bags.length ?? 0)) throw new TripCapacityError();
}

function mergeAuthority(base: unknown, local: unknown, remote: unknown): unknown {
  if (sameValue(local, base)) return remote;
  if (sameValue(remote, base) || sameValue(local, remote)) return local;
  throw new TripConflictError();
}

export function sameValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((value, index) => sameValue(value, b[index]));
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const left = a as Record<string, unknown>, right = b as Record<string, unknown>;
    return [...new Set([...Object.keys(left), ...Object.keys(right)])].every((key) => sameValue(left[key], right[key]));
  }
  return false;
}

function merge(base: unknown, local: unknown, remote: unknown): unknown {
  if (sameValue(local, base)) return remote;
  if (sameValue(remote, base) || sameValue(local, remote)) return local;
  if (Array.isArray(base) && Array.isArray(local) && Array.isArray(remote)) {
    const keyed = [...base, ...local, ...remote].every((value) => value && typeof value === 'object' && typeof value.id === 'string');
    if (keyed) {
      const ids = [...new Set([...remote, ...local].map((value) => value.id as string))];
      return ids.map((id) => merge(base.find((value) => value.id === id), local.find((value) => value.id === id), remote.find((value) => value.id === id))).filter((value) => value !== undefined);
    }
    // Ordered scalar arrays are consequential (e.g. extraction order); conflicting edits require review.
    throw new TripConflictError();
  }
  if (base && local && remote && typeof base === 'object' && typeof local === 'object' && typeof remote === 'object' && !Array.isArray(base)) {
    const b = base as Record<string, unknown>, l = local as Record<string, unknown>, r = remote as Record<string, unknown>;
    return Object.fromEntries([...new Set([...Object.keys(b), ...Object.keys(l), ...Object.keys(r)])].map((key) => [key, key === 'updatedAt' ? l[key] : key === 'remoteShare' ? mergeAuthority(b[key], l[key], r[key]) : merge(b[key], l[key], r[key])]));
  }
  throw new TripConflictError();
}

export function mergeTrip(base: PackingState, local: PackingState, remote: PackingState, rights: Rights = currentRights()): PackingState {
  if (base.trip.id !== local.trip.id || remote.trip.id !== local.trip.id) throw new TripConflictError();
  // Derived item/bag ID lists are rebuilt only after their entity collections merge successfully.
  const neutral = (state: PackingState) => ({ ...state, trip: { ...state.trip, itemInstanceIds: [], bagIds: [] } });
  const result = packingStateSchema.parse(merge(neutral(base), neutral(local), neutral(remote)));
  result.trip.itemInstanceIds = result.items.map((item) => item.id);
  result.trip.bagIds = result.bags.map((bag) => bag.id);
  if (validateStateReferences(result).length) throw new TripConflictError();
  assertTripCapacity(result, remote, rights);
  return result;
}
